import { useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from '@tanstack/react-router';

import { useGetPageProducts } from '@/api/hooks/queries/use-get-product-catalog-query';
import { Button } from '@/components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { useProductSelectionStep } from '@/hooks/use-product-selection-step';
import { useSessionSave } from '@/hooks/use-session-save';
import {
  addProductIds,
  removeProductIds,
  useSessionStore,
} from '@/hooks/use-session-store/use-session-store';
import { LOGGER_ACTIONS, logger } from '@/lib/logger';
import { PRODUCT_STEPS } from '@/lib/products.ts';
import { getActionablePlanFamilyCarrierTypes } from '@/types/insurance-builder.type.ts';
import { CATEGORY_TYPE } from '@/types/product.type.ts';
import ProductStepper from '@/views/layouts/ProductStepper.tsx';
import ProductRadioGroup from '@/views/products/components/ProductRadioGroup.tsx';
import { ProductSelectionSubmitFeedback } from '@/views/products/components/ProductSelectionSubmitFeedback.tsx';
import SkipButton from '@/views/products/components/SkipButton.tsx';

export default function SelectMobility() {
  const { saveSession } = useSessionSave();
  const { insuranceBuilder, selectedProductIds } = useSessionStore();

  const { products, isSuccess } = useGetPageProducts({
    categoryType: CATEGORY_TYPE.MOBILITY,
    patientCarrierTypes: getActionablePlanFamilyCarrierTypes(insuranceBuilder),
  });

  const mobilityId = useMemo(() => {
    return products.find((p) => selectedProductIds.includes(p.uuid))?.uuid ?? '';
  }, [products, selectedProductIds]);

  const updateMobility = useCallback(
    (productId: string) => {
      // Handle deselection - remove all mobility products
      if (!productId) {
        removeProductIds(products.map((p) => p.uuid));
        return;
      }
      // Handle selection - remove others and add selected
      const otherMobility = products.filter((p) => p.uuid !== productId);
      removeProductIds(otherMobility.map((p) => p.uuid));
      addProductIds(productId);
    },
    [products]
  );

  const numProducts = useMemo(() => {
    return products.filter((p) => selectedProductIds.includes(p.uuid)).length;
  }, [products, selectedProductIds]);

  const navigate = useNavigate();
  const { nextStep: nextPath } = useProductSelectionStep();
  const skipSelection = () => {
    removeProductIds(products.map((p) => p.uuid));
    // Auto-save session (non-blocking)
    saveSession();

    logger.action(LOGGER_ACTIONS.INTAKE.PRODUCT_SELECTION_SKIPPED, {
      product_category: CATEGORY_TYPE.MOBILITY,
    });
    navigate({ to: nextPath });
  };

  const handleAddToOrder = () => {
    // Auto-save session (non-blocking)
    saveSession();
    navigate({ to: nextPath });
  };

  const noEligibleProducts = isSuccess && products.length === 0;
  if (noEligibleProducts) {
    return <Navigate to={nextPath} replace />;
  }

  return (
    <section>
      <ProductStepper steps={PRODUCT_STEPS} />
      <div className="space-y-6">
        <Card spacing="sm">
          <CardHeader>
            <CardTitle>
              Choose <span className="text-orchid-700">one</span> mobility aid
            </CardTitle>
            <CardDescription>
              Medicare covers <b>only one</b> mobility aid <b>every 5 years</b>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ProductRadioGroup
              products={products}
              value={mobilityId}
              onValueChange={updateMobility}
            />
          </CardContent>
        </Card>

        <div className="grid grid-cols-[1fr_3fr] gap-3">
          <SkipButton categoryType={CATEGORY_TYPE.MOBILITY} onSkip={skipSelection} />
          <Button onClick={handleAddToOrder}>Add to order ({numProducts})</Button>
        </div>
        <ProductSelectionSubmitFeedback currentCategory={CATEGORY_TYPE.MOBILITY} />
      </div>
    </section>
  );
}
